import { BusinessRegistrationForm } from '@/types/businessRegistration';

export const validatePersonalStep = (formData: BusinessRegistrationForm) => {
  const errors: { [key: string]: string } = {};

  // Personal Data Validation Only
  if (!formData.userData.email) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  if (!formData.userData.password) {
    errors.password = 'Password is required';
  } else if (formData.userData.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }
  
  if (!formData.userData.name || formData.userData.name.trim().length < 2) {
    errors.name = 'Full name is required (minimum 2 characters)';
  }
  
  if (!formData.userData.nationality) {
    errors.nationality = 'Nationality is required';
  }
  
  if (!formData.userData.dateOfBirth) {
    errors.dateOfBirth = 'Date of birth is required';
  } else {
    // Check if user is at least 18 years old
    const today = new Date();
    const birthDate = new Date(formData.userData.dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 18) {
      errors.dateOfBirth = 'You must be at least 18 years old to register';
    }
  }

  return errors;
};

export const validateBusinessStep = (formData: BusinessRegistrationForm) => {
  const errors: { [key: string]: string } = {};

  // Business Data Validation Only
  if (!formData.businessData.name || formData.businessData.name.trim().length < 2) {
    errors.businessName = 'Business name is required (minimum 2 characters)';
  }
  
  if (!formData.businessData.description || formData.businessData.description.trim().length < 10) {
    errors.description = 'Please provide a detailed description (minimum 10 characters)';
  }

  // Validate that at least one activity type is selected
  if (!formData.businessData.hasAnimalActivities && !formData.businessData.hasAdventureActivities) {
    errors.activityTypes = 'Please select at least one type of activity your business offers';
  }

  // Validate business type is selected when activity types are chosen
  if ((formData.businessData.hasAnimalActivities || formData.businessData.hasAdventureActivities) && !formData.businessData.type) {
    errors.businessType = 'Please select a business category';
  }

  // Validate emoji is selected when activity types are chosen
  if ((formData.businessData.hasAnimalActivities || formData.businessData.hasAdventureActivities) && !formData.businessData.emoji) {
    errors.emoji = 'Please select a business icon';
  }

  return errors;
};

export const validateContactStep = (formData: BusinessRegistrationForm) => {
  const errors: { [key: string]: string } = {};

  // Contact Validation ONLY
  if (!formData.businessData.contact.email) errors.businessEmail = 'Business email is required';
  
  // Also validate email format
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessData.contact.email)) {
    errors.businessEmail = 'Please enter a valid email address';
  }
  
  if (!formData.businessData.contact.phone) errors.phone = 'Phone number is required';
  
  // Check if website URL is valid (if provided)
  if (formData.businessData.contact.website && 
      !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}$/i
      .test(formData.businessData.contact.website)) {
    errors.website = 'Please enter a valid website URL';
  }

  return errors;
};

// Create a separate function for location validation
export const validateLocationStep = (formData: BusinessRegistrationForm) => {
  const errors: { [key: string]: string } = {};

  // Location Validation ONLY
  const { address } = formData.businessData.location;
  if (!address.street) errors.street = 'Street is required';
  if (!address.city) errors.city = 'City is required';
  if (!address.state) errors.state = 'State is required';
  if (!address.country) errors.country = 'Country is required';
  if (!address.postalCode) errors.postalCode = 'Postal code is required';

  return errors;
};

// Add experience validation if needed
export const validateExperienceStep = (formData: BusinessRegistrationForm) => {
  const errors: { [key: string]: string } = {};

  // Check if at least one experience is selected
  if (!formData.businessData.experiences || formData.businessData.experiences.length === 0) {
    errors.experiences = 'Please select at least one type of experience';
  }

  return errors;
};